// src/pages/ContactPage.jsx
import React from "react";
import { useLocation } from "react-router-dom";
import NavBar from "../components/layout/NavBar";
import Footer from "../components/layout/Footer";
import bgHero from "../images/contact.JPEG";
import { FaInstagram, FaFacebook, FaLinkedin } from "react-icons/fa";
import emailjs from "@emailjs/browser";

export default function ContactPage() {
  const [formType, setFormType] = React.useState("contact"); // "contact" | "partenaire"
  const [loading, setLoading] = React.useState(false);
  const [sent, setSent] = React.useState(false);
  const [error, setError] = React.useState("");
  const formRef = React.useRef(null);

  const location = useLocation();

  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    const type = params.get("type");
    if (type && ["contact", "partenaire"].includes(type)) {
      setFormType(type);
    }
  }, [location.search]);

  const TITLES = {
    contact: {
      h2: "Une question ? Parlons-en !",
      sub: "Dites-nous ce dont vous avez besoin — on revient vers vous très vite.",
    },
    partenaire: {
      h2: "Prêt à devenir partenaire ?",
      sub: "Expliquez-nous votre activité et vos objectifs — on vous accompagne pas à pas.",
    },
  };


    function onSubmit(e) {
      e.preventDefault();
      setLoading(true);

      emailjs.sendForm(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        e.target,
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY
      )
      .then(() => {
        setLoading(false);
        setSent(true);
        e.target.reset();
        setTimeout(() => setSent(false), 3000);
      })
      .catch((err) => {
        setLoading(false);
        console.error("Erreur EmailJS:", err);
        alert("Une erreur est survenue, réessayez plus tard.");
      });
    }

  return (
    <div className="min-h-screen bg-white text-black">
      <NavBar />

      {/* HERO */}
      <section className="mx-auto max-w-[1519px] px-4 sm:px-6 lg:px-8 mt-6">
        <div className="relative overflow rounded-3xl shadow-lg">
          <div aria-hidden className="absolute inset-0 rounded-3xl bg-gradient-to-t from-[#E35EA7] to-[#FFDDEA]" />
          <div
            aria-hidden
            className="absolute inset-0 rounded-3xl bg-center bg-cover opacity-60"
            style={{ backgroundImage: `url(${bgHero})` }}
          />
          <div aria-hidden className="absolute inset-0 rounded-3xl bg-black/10" />
          <div className="relative flex flex-col items-center justify-center text-center p-5 sm:p-8 lg:p-12 min-h-[620px] lg:min-h-[840px]">
            <h1 className="text-3xl sm:text-5xl font-asap font-semibold text-white">Contactez-nous</h1>
            <p className="mt-3 max-w-3xl text-white/90 text-sm sm:text-base">
              Une question, une idée ou l’envie de rejoindre le réseau ? Choisissez le type
              de demande et envoyez-nous un message — on vous répond rapidement.
            </p>

            <div className="mt-6 flex items-center justify-center gap-3">
              <a
                href="#form"
                className="inline-flex items-center justify-center rounded-full bg-black text-white px-6 py-3 text-sm font-medium shadow-[0_10px_24px_rgba(0,0,0,0.35)] hover:opacity-90 transition"
              >
                Appelez nous !
              </a>
              <a
                href="#infos"
                className="inline-flex items-center justify-center rounded-full bg-white text-neutral-900 px-6 py-3 text-sm font-medium ring-1 ring-black/10 hover:bg-white/95 transition"
              >
                Nos informations
              </a>
            </div>
          </div>
          <div aria-hidden className="pointer-events-none absolute inset-0 rounded-3xl ring-1 ring-white/30" />
        </div>
      </section>

      {/* FORMULAIRE — CENTRÉ */}
      <section id="form" className="mx-auto max-w-[900px] px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
        {/* Onglets */}
        <div className="text-center">
          <div className="inline-flex flex-wrap items-center justify-center gap-2">
            {[
              { value: "contact", label: "Contact" },
              { value: "partenaire", label: "Devenir partenaire" },
            ].map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setFormType(opt.value)}
                className={`rounded-full px-4 py-2 text-sm font-medium transition ${
                  formType === opt.value
                    ? "bg-black text-white shadow-[0_10px_24px_rgba(0,0,0,0.35)]"
                    : "bg-white text-neutral-900 ring-1 ring-black/10 hover:bg-white/95"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          <h2 className="mt-6 text-2xl sm:text-3xl font-asap font-semibold">{TITLES[formType].h2}</h2>
          <p className="mt-2 text-neutral-600 text-[15px]">{TITLES[formType].sub}</p>
        </div>

        <div className="mt-8 rounded-3xl border border-black/10 bg-white shadow-[0_10px_30px_rgba(0,0,0,0.08)] p-5 sm:p-7">
          <form ref={formRef} onSubmit={onSubmit} className="space-y-6">
            <input type="hidden" name="form_type" value={formType} />
            <input type="hidden" name="page" value={window.location.href} />
            <input type="text" name="website" className="hidden" tabIndex={-1} autoComplete="off" />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Input name="from_name" placeholder="Votre prénom" required />
              <Input name="from_lastname" placeholder="Votre nom" required />
              <Input name="phone" placeholder="Numéro de téléphone" className="sm:col-span-2"/>
              <Input name="from_email" type="email" placeholder="Email" className="sm:col-span-2" required />
              {formType === "partenaire" && (
                <Input name="company" placeholder="Nom du commerce / entreprise" className="sm:col-span-2" />
              )}
            </div>

            <Textarea
              name="message"
              rows={6}
              placeholder={
                formType === "partenaire"
                  ? "Décrivez votre activité, vos objectifs et ce que vous attendez du PACTE…"
                  : "Décrivez votre demande (contexte, objectifs)…"
              }
              required
            />

            {/* RGPD */}
            <p className="text-xs text-neutral-500 leading-relaxed">
              Les informations saisies sont utilisées uniquement pour répondre à votre demande.
              Vous pouvez exercer vos droits en nous écrivant à{" "}
              <a href="mailto:contact@pacte-claix.fr" className="underline">
                contact@pacte-claix.fr
              </a>
              . En savoir plus : <a href="/confidentialite" className="underline">politique de confidentialité</a>.
            </p>

            {error && (
              <div className="rounded-xl border border-rose-200 bg-rose-50 text-rose-700 px-4 py-3 text-sm">
                {error}
              </div>
            )}
            {sent && (
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 text-emerald-700 px-4 py-3 text-sm">
                Merci ! Votre demande a bien été envoyée.
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full inline-flex items-center justify-center rounded-full bg-black text-white px-6 py-3 text-sm font-medium shadow-[0_10px_24px_rgba(0,0,0,0.35)] hover:opacity-90 transition disabled:opacity-60"
            >
              {loading ? "Envoi…" : "Envoyer ma demande"}
            </button>
          </form>
        </div>
      </section>

      {/* INFOS */}
      <section id="infos" className="mx-auto max-w-[900px] px-4 sm:px-6 lg:px-8 pb-12 sm:pb-16">
        <h3 className="text-xl sm:text-2xl font-asap font-semibold">Nos informations</h3>

        <div className="mt-6 space-y-5 text-[15px]">
          <InfoRow label="Email">
            <a href="mailto:contact@pacte-claix.fr" className="font-medium hover:underline">
              contact@pacte-claix.fr
            </a>
          </InfoRow>

          <InfoRow label="Réseaux">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-neutral-800">
              <FaLinkedin size={18} />
              <a href="https://www.linkedin.com/company/pacte-claix/" className="hover:opacity-80">LinkedIn</a>
              <span className="text-neutral-300">·</span>
              <FaInstagram size={18} />
              <a href="https://www.instagram.com/pacte_claix_38" className="hover:opacity-80">Instagram</a>
              <span className="text-neutral-300">·</span>
              <FaFacebook size={18} />
              <a href="https://www.facebook.com/people/PACTE/61564146676538/" className="hover:opacity-80">Facebook</a>
            </div>
          </InfoRow>

          <InfoRow label="Dons">
            <a
              href="https://www.helloasso.com/associations/promouvons-nos-acteurs-claixois-tous-ensemble/formulaires/1"
              className="font-medium hover:underline"
            >
              HelloAsso
            </a>
          </InfoRow>
        </div>
      </section>

      <Footer />
    </div>
  );
}

/* Helpers */
function InfoRow({ label, children }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:gap-4">
      <div className="text-sm text-neutral-500 sm:w-48">{label}</div>
      <div>{children}</div>
    </div>
  );
}

function Input({ className = "", ...props }) {
  return (
    <input
      {...props}
      className={`w-full rounded-xl border border-neutral-200 bg-white px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-black/10 ${className}`}
    />
  );
}

function Textarea({ className = "", ...props }) {
  return (
    <textarea
      {...props}
      className={`w-full rounded-xl border border-neutral-200 bg-white px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-black/10 ${className}`}
    />
  );
}